/**
 * Shared Tours Configuration
 * Centralized tour definitions used across What's New and Learning Center
 */
import { RoleLevel } from '../types/rbacImproved';
/**
 * Certificate Verification Tour
 * Used in both What's New and Learning Center
 */
export const CERTIFICATE_VERIFICATION_TOUR = {
    tourId: 'certificate-verification-tour',
    title: 'Certificate Verification Feature',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-first-diamond',
            content: 'Welcome to the Royal Gems Platform! Let me show you how to verify diamond certificates. This is a diamond card showing all key information.',
            title: 'Certificate Verification Tutorial',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-specs',
            content: 'Each diamond shows its lab certification (GIA, AGS, IGI, HRD, EGL USA, or GCAL).',
            title: 'Certification Details',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-certificate-button',
            content: 'For certified diamonds, you\'ll see the "View Certificate" button.',
            title: 'Certificate Button',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-certificate-button',
            content: 'Click to open the official lab website and verify authenticity. Works with GIA, AGS, IGI, HRD, EGL USA, and GCAL certificates.',
            title: 'Verify Authenticity',
            placement: 'top',
            disableBeacon: true,
            spotlightClicks: true
        }
    ]
};
/**
 * Advanced Search Filters Tour
 */
export const ADVANCED_SEARCH_TOUR = {
    tourId: 'advanced-search-tour',
    title: 'Advanced Search Filters',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-filter-panel',
            content: 'Welcome to the Advanced Search! Use these powerful filters to find exactly the diamonds you need.',
            title: 'Filter Panel',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-shape-filter',
            content: 'Filter by diamond shape - Round, Princess, Emerald, and more.',
            title: 'Shape Filter',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-color-filter',
            content: 'Select color grades from D (colorless) to Z (light yellow).',
            title: 'Color Filter',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-clarity-filter',
            content: 'Choose clarity grades - FL, IF, VVS1, VVS2, VS1, VS2, SI1, SI2, I1.',
            title: 'Clarity Filter',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-carat-filter',
            content: 'Set minimum and maximum carat weight range.',
            title: 'Carat Range',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-lab-filter',
            content: 'Filter by certification lab - GIA, AGS, IGI, HRD, EGL USA, GCAL.',
            title: 'Lab Certification',
            placement: 'right',
            disableBeacon: true
        }
    ]
};
/**
 * Diamond Browsing & Navigation Tour
 * Chapter 2 of Platform Essentials
 */
export const DIAMOND_BROWSING_TOUR = {
    tourId: 'diamond-browsing-tour',
    title: 'Diamond Browsing & Navigation',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-pagination-sidebar',
            content: 'The sticky sidebar shows all available pages. You can browse through thousands of diamonds without losing your place!',
            title: 'Sticky Pagination',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-page-number',
            content: 'Click any page number to jump directly to that page - no more clicking "Next" repeatedly!',
            title: 'Quick Page Navigation',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-current-page',
            content: 'The current page is highlighted so you always know where you are in the catalog.',
            title: 'Current Page Indicator',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-first-diamond',
            content: 'Browse diamonds in a beautiful grid layout. Each card shows key information at a glance - shape, carat, color, clarity, cut, lab, and pricing.',
            title: 'Diamond Grid Layout',
            placement: 'right',
            disableBeacon: true
        }
    ]
};
/**
 * Basic Diamond Information Tour
 * Chapter 3 of Platform Essentials
 */
export const BASIC_DIAMOND_INFO_TOUR = {
    tourId: 'basic-diamond-info-tour',
    title: 'Basic Diamond Information',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-diamond-specs',
            content: 'Every diamond shows the 4Cs - the key factors that determine quality and value. Let\'s break them down!',
            title: 'The 4Cs',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-carat',
            content: 'Carat (ct) measures the diamond\'s weight. 1 carat = 200 milligrams. Larger carats generally mean higher value.',
            title: 'Understanding Carat',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-color',
            content: 'Color grade ranges from D (colorless) to Z (light yellow). D-F are considered colorless and most valuable.',
            title: 'Color Grading',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-clarity',
            content: 'Clarity measures internal inclusions and external blemishes. FL (Flawless) is rarest, while VS1-SI1 offers good value for size.',
            title: 'Clarity Scale',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-cut',
            content: 'Cut quality affects how well the diamond reflects light. Excellent and Very Good cuts maximize sparkle and brilliance.',
            title: 'Cut Quality',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-diamond-price',
            content: 'View pricing information based on your dealer level. Prices are shown in USD and update in real-time based on market conditions.',
            title: 'Pricing Information',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Memo Request Tracking Tour
 */
export const MEMO_TRACKING_TOUR = {
    tourId: 'memo-tracking-tour',
    title: 'Memo Request Tracking',
    route: '/dealer-dashboard',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-memo-button',
            content: 'Request diamonds on memo directly from the diamond card. Perfect for showing to clients!',
            title: 'Request Memo',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-active-memos',
            content: 'Track all your active memo requests in one place with real-time status updates.',
            title: 'Active Memos',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-memo-status',
            content: 'See approval status, expected delivery dates, and due dates for each memo.',
            title: 'Memo Status',
            placement: 'right',
            disableBeacon: true
        }
    ]
};
/**
 * Diamond Comparison Tool Tour
 */
export const COMPARISON_TOOL_TOUR = {
    tourId: 'comparison-tool-tour',
    title: 'Diamond Comparison Tool',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-compare-checkbox',
            content: 'Select the checkbox on any diamond to add it to your comparison list (up to 4 diamonds).',
            title: 'Select Diamonds',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-compare-button',
            content: 'Once you\'ve selected 2 or more diamonds, click here to compare them side-by-side.',
            title: 'Compare Button',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-comparison-modal',
            content: 'View detailed side-by-side comparison of specs, pricing, and certifications.',
            title: 'Comparison View',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Personal Watchlist Tour
 */
export const WATCHLIST_TOUR = {
    tourId: 'watchlist-tour',
    title: 'Personal Watchlist',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-heart-icon',
            content: 'Click the heart icon on any diamond to save it to your personal watchlist.',
            title: 'Add to Watchlist',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-watchlist-tab',
            content: 'Access your saved diamonds anytime from the Watchlist tab.',
            title: 'Watchlist Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-watchlist-count',
            content: 'See how many diamonds you\'ve saved at a glance.',
            title: 'Watchlist Count',
            placement: 'bottom',
            disableBeacon: true
        }
    ]
};
/**
 * Learning Center Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the new Learning Center feature
 */
export const LEARNING_CENTER_SHOWCASE_TOUR = {
    tourId: 'learning-center-showcase-tour',
    title: 'Learning Center Tour',
    route: '/learning-center',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-progress-overview',
            content: 'Welcome to the new Learning Center! Track your overall progress - see how many tutorials you\'ve completed and badges you\'ve earned.',
            title: 'Your Progress Dashboard',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-tutorial-card',
            content: 'Each tutorial shows difficulty level, estimated time, number of chapters, and the badge you\'ll earn on completion.',
            title: 'Tutorial Cards',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-start-tutorial-button',
            content: 'Click "Start Tutorial" to begin! The system automatically saves your progress as you complete each chapter. Chapters flow seamlessly one after another.',
            title: 'Start Learning',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Memo Dashboard Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the Memo Tracking Dashboard
 */
export const MEMO_DASHBOARD_SHOWCASE_TOUR = {
    tourId: 'memo-dashboard-showcase-tour',
    title: 'Memo Dashboard Tour',
    route: '/memos',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-memo-stats',
            content: 'Welcome to your Memo Dashboard! Track all your diamond loan requests - pending, approved, and completed.',
            title: 'Your Dashboard',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-search-sort',
            content: 'Find specific memos quickly by searching for diamond ID or certificate number. Filter by status to focus on what matters.',
            title: 'Search & Filter',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-memo-card',
            content: 'Each row shows the diamond you requested, its price, current status, and when it\'s due back to Royal Gems.',
            title: 'Your Memos',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-memo-actions',
            content: 'View details or cancel pending requests. Need help with an approved memo? Contact Royal Gems directly.',
            title: 'Actions',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Export Search Results Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the Export functionality
 */
export const EXPORT_SHOWCASE_TOUR = {
    tourId: 'export-showcase-tour',
    title: 'Export Search Results Tour',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SENIOR_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-filter-section',
            content: 'Start by filtering diamonds using shape, color, clarity, cut quality, lab, carat range, and price range to find exactly what you need.',
            title: 'Filter Your Search',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-export-button',
            content: 'Once you\'ve filtered the diamonds you want, click "Export Results" to download them. Available for Senior Dealers and above.',
            title: 'Export Button',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-export-button',
            content: 'Choose between CSV (perfect for Excel and Google Sheets) or JSON format. The export includes all diamond specifications, pricing, and certification data.',
            title: 'Export Formats',
            placement: 'bottom',
            disableBeacon: true
        }
    ]
};
/**
 * Dealer Analytics Showcase Tour
 */
export const DEALER_ANALYTICS_SHOWCASE_TOUR = {
    tourId: 'dealer-analytics-showcase-tour',
    title: 'Dealer Performance Analytics Tour',
    route: '/admin/dealer-analytics',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-dealer-selector',
            content: 'Select any dealer from the dropdown to view their performance analytics. The system will load all their memo request data and calculate comprehensive statistics.',
            title: 'Select a Dealer',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-performance-metrics',
            content: 'Key performance metrics show approval rate (percentage of requests approved), conversion rate (sold vs returned), average memo value, response time, and overdue count. Green highlights indicate strong performance (80%+ approval, 30%+ conversion).',
            title: 'Performance Metrics',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-shape-preferences',
            content: 'Diamond shape preference charts reveal which shapes each dealer requests most frequently. Use these insights to stock inventory that matches dealer preferences and improve approval rates.',
            title: 'Shape Preferences',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-color-preferences',
            content: 'Color and clarity charts show what grades dealers prefer. Understanding these patterns helps you optimize your inventory selection for each dealer.',
            title: 'Color & Clarity Preferences',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-activity-timeline',
            content: 'The recent activity timeline shows the last 10 memo requests with dates, diamond details, and values. Track dealer engagement patterns and identify your most active dealers.',
            title: 'Activity Timeline',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Memo Request Tour
 * Chapter 1 of Memo System Basics
 */
export const MEMO_REQUEST_TOUR = {
    tourId: 'memo-request-tour',
    title: 'Requesting a Memo',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-first-diamond',
            content: 'Welcome to the Memo System! A memo lets you borrow diamonds from Royal Gems to evaluate or show to potential buyers. Let\'s request one!',
            title: 'What is a Memo?',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: '#tour-view-details',
            content: 'Click "View" to see the full diamond details. From there, you can request a memo.',
            title: 'View Diamond Details',
            placement: 'top',
            disableBeacon: true,
            spotlightClicks: true
        }
    ]
};
/**
 * Memo Request Modal Tour - Runs inside DiamondDetailModal
 * Auto-started when DiamondDetailModal opens during memo request tutorial
 */
export const MEMO_DETAIL_MODAL_TOUR = {
    tourId: 'memo-detail-modal-tour',
    title: 'Request Memo',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    showProgress: true,
    showSkipButton: true,
    disableOverlay: true, // Disable overlay for modal tours - spotlight positioning unreliable when page is scrolled
    steps: [
        {
            target: '#tour-request-memo-button',
            content: 'This button lets you request a memo to borrow this diamond from Royal Gems. Click "Next" to continue to the request form.',
            title: 'Request Memo Button',
            placement: 'top',
            disableBeacon: true,
            spotlightClicks: false // Don't allow clicking - tour chain handles opening the memo modal
        }
    ]
};
/**
 * Memo Form Tour - Runs inside RequestMemoModal
 * Auto-started when RequestMemoModal opens during memo request tutorial
 */
export const MEMO_FORM_TOUR = {
    tourId: 'memo-form-tour',
    title: 'Complete Request',
    route: '/atelier',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    showProgress: true,
    showSkipButton: true,
    disableOverlay: true, // Disable overlay for modal tours - spotlight positioning unreliable when page is scrolled
    steps: [
        {
            target: '#clientName',
            content: 'Enter your name or company name. This identifies who is requesting the diamond.',
            title: 'Your Information',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#purpose',
            content: 'Why do you need this diamond? Select "viewing" to evaluate it, or "appraisal" for professional assessment.',
            title: 'Memo Purpose',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-memo-submit',
            content: 'Submit your request! Royal Gems will review and notify you when approved. Track all requests in the Memo Dashboard.',
            title: 'Submit Request',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Memo Status Tracking Tour
 * Chapter 2 of Memo System Basics
 */
export const MEMO_STATUS_TOUR = {
    tourId: 'memo-status-tour',
    title: 'Tracking Memo Status',
    route: '/memos',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-memo-stats',
            content: 'This is your Memo Dashboard! See all your memo requests at a glance - pending, approved, and completed.',
            title: 'Your Memo Dashboard',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-search-sort',
            content: 'Looking for a specific memo? Search by diamond ID or certificate number. You can also filter by status to see only pending or approved memos.',
            title: 'Search & Filter',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-memo-card',
            content: 'Each row shows the diamond details, price, and current status. Pending means Royal Gems is reviewing your request. Approved means the diamond is on its way!',
            title: 'Understanding Status',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-memo-card',
            content: 'Keep an eye on due dates! Return diamonds on time to maintain your good standing with Royal Gems.',
            title: 'Due Dates',
            placement: 'right',
            disableBeacon: true
        }
    ]
};
/**
 * Memo Management Tour
 * Chapter 3 of Memo System Basics
 */
export const MEMO_MANAGEMENT_TOUR = {
    tourId: 'memo-management-tour',
    title: 'Managing Your Memos',
    route: '/memos',
    requiresAuth: true,
    minRoleLevel: RoleLevel.BASIC_DEALER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-memo-actions',
            content: 'Use these buttons to manage your memo requests. What you can do depends on the current status.',
            title: 'Action Buttons',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-memo-actions',
            content: 'Click "Details" to see the full diamond information, your request date, and due date for return.',
            title: 'View Details',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-memo-actions',
            content: 'Changed your mind? Cancel pending requests before Royal Gems approves them. For approved memos, contact Royal Gems directly.',
            title: 'Cancel Requests',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-memo-stats',
            content: 'Your memo statistics show how many requests are pending, approved, or completed. Use this to stay organized!',
            title: 'Your Statistics',
            placement: 'bottom',
            disableBeacon: true
        }
    ]
};
/**
 * RG Appraisal Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the RG Appraisal tool
 */
export const RG_APPRAISAL_SHOWCASE_TOUR = {
    tourId: 'rg-appraisal-showcase-tour',
    title: 'RG Appraisal Tool Tour',
    route: '/admin/appraisal-maker',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-stats-dashboard',
            content: 'Welcome to RG Appraisal! Track all your appraisal activity at a glance - total appraisals created, total value appraised, average value per piece, and recent activity.',
            title: 'Statistics Dashboard',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-create-tab',
            content: 'Create new appraisals for finished jewelry pieces using our step-by-step wizard. The system guides you through customer info, jewelry details, gemstones, photos, and final review.',
            title: 'Create Appraisal',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-wizard-progress',
            content: 'The wizard shows your progress with visual steps and percentage complete. Each step validates required fields before letting you advance.',
            title: 'Wizard Progress',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-history-tab',
            content: 'View all past appraisals with powerful search, filter, and sort capabilities. Filter by status (Draft, Completed, Sent, Archived) and date ranges.',
            title: 'Appraisal History',
            placement: 'bottom',
            disableBeacon: true,
            spotlightClicks: true
        },
        {
            target: '#tour-filter-bar',
            content: 'Search by customer name or appraisal number, filter by status and date range, sort by date/value/customer, and export everything to CSV for external analysis.',
            title: 'Advanced Filtering',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-first-appraisal',
            content: 'Each appraisal card shows full details: customer info, jewelry type, metal specifications, gemstone count, total value, and template used. Quick actions let you view PDF, email customer, duplicate, edit, or delete.',
            title: 'Appraisal Management',
            placement: 'right',
            disableBeacon: true
        }
    ]
};
/**
 * Financial Hub Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the unified Financial Management Hub
 */
export const FINANCIAL_HUB_SHOWCASE_TOUR = {
    tourId: 'financial-hub-showcase-tour',
    title: 'Financial Management Hub Tour',
    route: '/admin/financials',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: 'body',
            content: 'Welcome to the Financial Management Hub! This unified interface consolidates all financial operations into one seamless experience - suppliers, purchases, payments, and invoices all in one place.',
            title: 'Financial Hub Overview',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-dashboard-tab',
            content: 'The Dashboard provides a financial overview with key metrics: Accounts Payable (what we owe suppliers), Accounts Receivable (what customers owe us), Net Cash Position, and Action Items requiring immediate attention.',
            title: 'Dashboard Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-suppliers-tab',
            content: 'The Suppliers tab manages all supplier contacts - add new suppliers, track purchases from each supplier, view payment history, and maintain complete contact information.',
            title: 'Suppliers Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-purchases-tab',
            content: 'The Purchases tab tracks all inventory purchases from suppliers - record new purchases with line items, track payment status, view purchase history, and manage costs with tax and shipping.',
            title: 'Purchases Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-payments-tab',
            content: 'The Payments tab handles both Accounts Payable (supplier payments) and Accounts Receivable (customer payments) - record payments, verify amounts, track cleared payments, and sync with purchases automatically.',
            title: 'Payments Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-invoices-tab',
            content: 'The Invoices tab manages customer billing - view automatically generated invoices from approved memos, download PDFs, send emails, track payment status, and mark as paid.',
            title: 'Invoices Tab',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-mock-toggle',
            content: 'Toggle Mock Data mode to instantly populate the hub with realistic demo data - 12 suppliers, 25 purchases, 15 payments, and 10 invoices. Perfect for testing and demonstrations!',
            title: 'Demo Mode',
            placement: 'left',
            disableBeacon: true
        }
    ]
};
/**
 * Invoice Management Showcase Tour (STANDALONE - No badge tracking)
 * Used in What's New to showcase the automated invoicing system
 */
export const INVOICE_MANAGEMENT_SHOWCASE_TOUR = {
    tourId: 'invoice-management-showcase-tour',
    title: 'Automated Invoicing Tour',
    route: '/admin/financials',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: 'body',
            content: 'Welcome to the Financial Management Hub! This unified interface manages all financial operations. Navigate to the Invoices tab to see how automated invoicing works - the system creates professional invoices when you approve memo requests.',
            title: 'Financial Hub - Invoices',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '.stat-value',
            content: 'Track your invoice metrics at a glance - total invoices, total value, amount paid, and outstanding balance. Stay on top of your receivables.',
            title: 'Invoice Statistics',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#search',
            content: 'Search invoices by invoice number, customer name, or email. Combine with status filters for powerful invoice tracking.',
            title: 'Search & Filter',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '[id^="invoice-"]',
            content: 'Each invoice card shows full details: invoice number, customer info, dates, payment terms, and total amount. Status badges show paid, issued, or overdue at a glance.',
            title: 'Invoice Details',
            placement: 'right',
            disableBeacon: true
        },
        {
            target: 'button',
            content: 'Download professional PDF invoices to send to customers, email them directly from the platform, mark as paid when payment is received, or cancel if needed.',
            title: 'Invoice Actions',
            placement: 'top',
            disableBeacon: true
        }
    ]
};
/**
 * Pre-Registration System Showcase Tour
 * Used in What's New to showcase invitation-based user registration
 */
/**
 * Executive Command Center Dashboard Tour
 * Showcases new data-driven financial dashboard
 */
/**
 * Inventory Hub Showcase Tour
 * Showcases new unified inventory management system
 */
export const INVENTORY_HUB_SHOWCASE_TOUR = {
    tourId: 'inventory-hub-showcase-tour',
    title: 'Inventory Management Hub',
    route: '/admin/inventory',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-inventory-dashboard-tab',
            content: 'Welcome to the new Inventory Hub! A unified interface for managing your diamond inventory with professional analytics, inline actions, and seamless workflow.',
            title: 'Inventory Management Hub',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-mock-toggle',
            content: 'Toggle Mock Data to instantly load 150 realistic diamonds with proper distribution, certificates, and pricing. Perfect for demos and testing!',
            title: 'Mock Inventory Data',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-stats',
            content: 'Four key metrics track your inventory: Total Diamonds, Total Value (inventory valuation), Avg Price/Carat (quality indicator), and Avg Stock Days (turnover rate).',
            title: 'Inventory Metrics',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-charts',
            content: 'Interactive visualizations: Shape Distribution pie chart shows your inventory mix, and Stock Aging bar chart tracks how long diamonds have been in inventory.',
            title: 'Data Visualization',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-actions',
            content: 'Smart prioritization identifies actionable items: Slow-moving inventory (90+ days), High-value items ($50K+), Low-margin items (<20%), and Missing certificates. All organized by urgency.',
            title: 'Priority-Based Actions',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-diamonds-tab',
            content: 'The Diamonds tab shows your complete inventory list with advanced filtering, search, and inline detail modals. Seamlessly switches between real inventory and mock data.',
            title: 'Diamond Management',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-inventory-import-tab',
            content: 'The Import/Export tab handles RAPNET CSV imports and bulk operations. Upload CSV files to add diamonds to your inventory with automatic validation and processing.',
            title: 'Import & Export',
            placement: 'bottom',
            disableBeacon: true
        }
    ]
};
export const EXECUTIVE_DASHBOARD_SHOWCASE_TOUR = {
    tourId: 'executive-dashboard-showcase-tour',
    title: 'Executive Command Center',
    route: '/admin/financials',
    requiresAuth: true,
    minRoleLevel: RoleLevel.SALES_MANAGER,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-dashboard-tab',
            content: 'Welcome to the new Executive Command Center! Your redesigned financial dashboard with real-time metrics, data visualization, and intelligent action prioritization.',
            title: 'Executive Command Center',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-mock-toggle',
            content: 'Toggle Mock Data to instantly load 188 realistic transactions spanning 2 months of business activity. Perfect for demos and testing!',
            title: 'Mock Data Toggle',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-dashboard-stats',
            content: 'Four hero metrics track your cash flow in real-time: Accounts Receivable (what customers owe), Accounts Payable (what we owe suppliers), Net Cash Flow, and Daily Burn Rate. Trend indicators show week-over-week changes.',
            title: 'Real-Time Financial Metrics',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-dashboard-charts',
            content: 'Interactive data visualizations: 30-day cash flow trend (A/R vs A/P) and aging breakdown pie chart. Hover over data points for details.',
            title: 'Data Visualization',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-dashboard-actions',
            content: 'Smart prioritization organizes actions by urgency: Critical (90+ days), High Priority (45-90 days), Upcoming (15-45 days), and Pending Approvals. Each section is collapsible to reduce clutter.',
            title: 'Priority-Based Actions',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-dashboard-actions',
            content: 'Every action is fully functional with inline modals. Record payments, approve memos, send reminders - all without leaving the dashboard. The system auto-refreshes after each action!',
            title: 'Inline Actions & Modals',
            placement: 'top',
            disableBeacon: true,
            spotlightClicks: true
        }
    ]
};
export const PRE_REGISTRATION_SHOWCASE_TOUR = {
    tourId: 'pre-registration-showcase-tour',
    title: 'Pre-Registration System Tour',
    route: '/admin/users',
    requiresAuth: true,
    minRoleLevel: RoleLevel.PLATFORM_ADMIN,
    continuous: true,
    scrollToFirstStep: true,
    showProgress: true,
    showSkipButton: true,
    steps: [
        {
            target: '#tour-user-management-title',
            content: 'Welcome to User Management! The new Pre-Registration System lets admins create invitation links for streamlined user onboarding.',
            title: 'Pre-Registration System',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-create-registration-button',
            content: 'Click this button to create a unique registration link. You\'ll pre-fill the user\'s email, name, business details, and assign their role and permissions.',
            title: 'Create Registration Link',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-user-stats',
            content: 'Track your user base at a glance - total users, active users, pending approvals, and active dealers. The pre-registration system helps grow these numbers efficiently.',
            title: 'User Statistics',
            placement: 'bottom',
            disableBeacon: true
        },
        {
            target: '#tour-search-filter-bar',
            content: 'Search and filter existing users. Once pre-registered users complete their registration, they\'ll appear here with status "active" and role assigned by you.',
            title: 'User Search & Filter',
            placement: 'top',
            disableBeacon: true
        },
        {
            target: '#tour-create-registration-button',
            content: 'The generated link is valid for 7 days, can only be used once, and includes cryptographic security. Users only need to set a password - everything else is pre-filled!',
            title: 'Secure Invitation Links',
            placement: 'left',
            disableBeacon: true
        },
        {
            target: '#tour-user-management-title',
            content: 'Benefits: No manual data entry for new users, role assignment before registration, secure one-time links, password strength validation, and automatic login after registration completes.',
            title: 'Streamlined Onboarding',
            placement: 'bottom',
            disableBeacon: true
        }
    ]
};
/**
 * All available tours
 * Add new tours here to make them available across the platform
 */
export const TOURS = {
    CERTIFICATE_VERIFICATION: CERTIFICATE_VERIFICATION_TOUR,
    DIAMOND_BROWSING: DIAMOND_BROWSING_TOUR,
    BASIC_DIAMOND_INFO: BASIC_DIAMOND_INFO_TOUR,
    ADVANCED_SEARCH: ADVANCED_SEARCH_TOUR,
    MEMO_TRACKING: MEMO_TRACKING_TOUR,
    COMPARISON_TOOL: COMPARISON_TOOL_TOUR,
    WATCHLIST: WATCHLIST_TOUR,
    MEMO_REQUEST: MEMO_REQUEST_TOUR, // Chapter-based tutorial chapter 1
    MEMO_DETAIL_MODAL: MEMO_DETAIL_MODAL_TOUR, // Auto-started sub-tour for DiamondDetailModal
    MEMO_FORM: MEMO_FORM_TOUR, // Auto-started sub-tour for RequestMemoModal
    MEMO_STATUS: MEMO_STATUS_TOUR, // Chapter-based tutorial chapter 2
    MEMO_MANAGEMENT: MEMO_MANAGEMENT_TOUR, // Chapter-based tutorial chapter 3
    LEARNING_CENTER_SHOWCASE: LEARNING_CENTER_SHOWCASE_TOUR, // Standalone tour (no badge)
    MEMO_DASHBOARD_SHOWCASE: MEMO_DASHBOARD_SHOWCASE_TOUR, // Standalone tour (no badge)
    EXPORT_SHOWCASE: EXPORT_SHOWCASE_TOUR, // Standalone tour (no badge)
    DEALER_ANALYTICS_SHOWCASE: DEALER_ANALYTICS_SHOWCASE_TOUR, // Standalone tour (no badge)
    RG_APPRAISAL_SHOWCASE: RG_APPRAISAL_SHOWCASE_TOUR, // Standalone tour (no badge)
    FINANCIAL_HUB_SHOWCASE: FINANCIAL_HUB_SHOWCASE_TOUR, // Standalone tour (no badge)
    INVOICE_MANAGEMENT_SHOWCASE: INVOICE_MANAGEMENT_SHOWCASE_TOUR, // Standalone tour (no badge)
    PRE_REGISTRATION_SHOWCASE: PRE_REGISTRATION_SHOWCASE_TOUR, // Standalone tour (no badge)
    EXECUTIVE_DASHBOARD_SHOWCASE: EXECUTIVE_DASHBOARD_SHOWCASE_TOUR, // Standalone tour (no badge)
    INVENTORY_HUB_SHOWCASE: INVENTORY_HUB_SHOWCASE_TOUR // Standalone tour (no badge)
};
/**
 * Get tour by ID
 */
export const getTourById = (tourId) => {
    return Object.values(TOURS).find(tour => tour.tourId === tourId);
};
/**
 * Tutorial Metadata for Badge Awarding
 * Maps tour IDs to tutorial information for automatic badge awarding
 */
export const TOUR_TUTORIAL_MAP = {
// IMPORTANT: Only add STANDALONE tours here (e.g., What's New tours)
// Do NOT add chapter-based tours - they're handled by the Learning Center's chapter completion system
// Badges for chapter-based tutorials are awarded only after ALL chapters complete
// Example: If you have a standalone tour from What's New that's NOT part of a chapter system:
// 'some-standalone-tour': { tutorialId: '...', badge: {...} }
};
/**
 * Get tutorial metadata by tour ID
 */
export const getTutorialByTourId = (tourId) => {
    return TOUR_TUTORIAL_MAP[tourId];
};
